import React from "react";
import { Card, Box, CardContent, Typography, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

/**
 * Shared StatCard. matches the Dashboard crystal-icon style.
 *
 * Props:
 *   icon            ReactElement . icon to display in the crystal box
 *   title           string       . uppercase label
 *   count / value   any          . main numeric or text value
 *   subtitle        string       . optional caption below value
 *   percentage      number       . optional signed percentage (shows +/- trend)
 *   backgroundColor string       . accent/crystal color (default "#2f63ad")
 *   onHide          function     . if provided, renders a close button
 */
const StatCard = ({
  icon,
  title,
  count,
  value,
  subtitle,
  percentage,
  backgroundColor = "#2f63ad",
  onHide,
}) => {
  const displayValue = count !== undefined ? count : value;

  return (
    <Card
      sx={{
        borderRadius: "14px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
        px: 1,
        pt: 1,
        pb: 0.5,
        height: "100%",
        position: "relative",
        overflow: "hidden",
        backgroundColor: "rgba(255,255,255,0.96)",
        transition: "all 0.2s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 8px 18px rgba(0,0,0,0.18)",
        },
      }}
    >
      {/* Close Button */}
      {onHide && (
        <IconButton
          size="small"
          onClick={onHide}
          sx={{
            position: "absolute",
            top: 2,
            right: 2,
            color: "#9aa4b2",
            "&:hover": { color: "#e53935" },
          }}
        >
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      )}

      {/* Crystal Icon Box */}
      <Box
        sx={{
          position: "absolute",
          top: 12,
          left: 12,
          width: 36,
          height: 36,
          borderRadius: "10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: `${backgroundColor}18`,
          backdropFilter: "blur(8px)",
          border: `1px solid ${backgroundColor}35`,
          color: backgroundColor,
          boxShadow: "0 4px 10px rgba(0,0,0,0.06)",
        }}
      >
        {icon}
      </Box>

      {/* Content */}
      <CardContent
        sx={{
          marginLeft: 5,
          pt: 0.5,
          pb: "6px !important",
        }}
      >
        <Typography
          sx={{
            color: "#6b778c",
            fontWeight: 600,
            textTransform: "uppercase",
            fontSize: 9,
            lineHeight: 1,
          }}
        >
          {title}
        </Typography>

        <Typography
          sx={{
            fontWeight: 700,
            fontSize: 15,
            mt: 0.2,
            color: "#1f2d3d",
          }}
        >
          {displayValue}
        </Typography>

        {subtitle && (
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 500,
              color: "#8a94a6",
            }}
          >
            {subtitle}
          </Typography>
        )}

        {percentage !== undefined && (
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 500,
              color: percentage >= 0 ? "#43a047" : "#e53935",
            }}
          >
            {percentage >= 0 ? `+${percentage.toFixed(1)}%` : `${percentage.toFixed(1)}%`}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default StatCard;
